<template>
  <div class="report-date-range-picker">
    <SrpDropdown placement="bottom-end" :offset="3">
      <template #clickableElement="{ isDropdownVisible }">
        <div class="report-date-range-picker__trigger">
          <IconEmbedded name="calendar_2" :size="15" color="#5b5b5b" style="margin-right: 5px" />
          <LinkWithIcon isDottedUnderline color="gray">{{ triggerLabel }}</LinkWithIcon>
          <IconEmbedded :name="isDropdownVisible ? 'caret-top_3' : 'caret-bottom_3'" :size="14" color="#5b5b5b" style="margin-left: 2px" />
        </div>
      </template>

      <template #dropdown="{ close, computeDropdownPosition }">
        <div class="report-date-range-picker__dropdown">
          <div
            v-for="option in reportDateRangePresetOptions"
            :key="option.value"
            class="report-date-range-picker__option"
            :class="{ 'report-date-range-picker__option--selected': option.value === props.modelValue.preset }"
            @click="selectPreset(option.value, close, computeDropdownPosition)"
          >
            <span>{{ option.label }}</span>
            <IconEmbedded v-if="option.value === props.modelValue.preset" name="check_3" :size="16" color="rgba(0, 128, 96, 1)" />
          </div>

          <div v-if="isCalendarVisible" class="report-date-range-picker__calendar">
            <VueDatePicker
              :modelValue="customRange"
              format="MMM d yyyy"
              :weekStart="vueDatePickerWeekStart"
              :inline="{ input: false }"
              range
              :multiCalendars="isMultiCalendar"
              autoApply
              :enableTimePicker="false"
              :maxDate="today"
              @update:modelValue="dates => applyCustomRange(dates, close)"
            />
          </div>
        </div>
      </template>
    </SrpDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch, type Ref } from "vue";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import SrpDropdown from "@components/ui/SrpDropdown.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { vueDatePickerWeekStart } from "@logic/VueDatePickerConfig";

// Types
import type { ScreenSize } from "@contracts/screenSize";
import {
  DATE_RANGE_FORMAT,
  formatReportDateRange,
  reportDateRangePresetOptions,
  resolveReportDateRange,
  toReportDateString,
  type ReportDateRangePreset,
  type ReportDateRangeValue,
} from "./reportDateRange";

import DayjsExtended from "@logic/DayjsExtended";

const props = defineProps<{
  modelValue: ReportDateRangeValue;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: ReportDateRangeValue): void;
}>();

const screenSize = inject("screenSize") as Ref<ScreenSize>;

// Two months side by side everywhere but mobile, where the dropdown becomes a narrow modal.
const isMultiCalendar = computed<boolean>(() => screenSize?.value !== "mobile");

const today = new Date();

const triggerLabel = computed<string>(() => formatReportDateRange(props.modelValue));

// Custom range ===============================================================
/** Shown once "Custom range" is picked; stays open while the user adjusts the dates. */
const isCalendarVisible = ref<boolean>(props.modelValue.preset === "custom");

watch(
  () => props.modelValue.preset,
  preset => {
    if (preset === "custom") isCalendarVisible.value = true;
  }
);

const customRange = computed<Date[] | null>(() => {
  const { startDate, endDate } = props.modelValue;
  if (!startDate || !endDate) return null;
  const start = DayjsExtended(startDate, DATE_RANGE_FORMAT);
  const end = DayjsExtended(endDate, DATE_RANGE_FORMAT);
  if (!start.isValid() || !end.isValid()) return null;
  return [start.toDate(), end.toDate()];
});

function selectPreset(preset: ReportDateRangePreset, closeDropdown: () => void, computeDropdownPosition: () => void) {
  if (preset === "custom") {
    // Keep the dropdown open so the calendar can be used; seed it with the current window.
    isCalendarVisible.value = true;
    if (props.modelValue.preset !== "custom") {
      emit("update:modelValue", { preset: "custom", startDate: props.modelValue.startDate, endDate: props.modelValue.endDate });
    }
    computeDropdownPosition();
    return;
  }

  isCalendarVisible.value = false;
  emit("update:modelValue", resolveReportDateRange(preset));
  closeDropdown();
}

function applyCustomRange(dates: Array<Date | null> | null, closeDropdown: () => void) {
  if (!dates || !dates[0] || !dates[1]) return;

  emit("update:modelValue", { preset: "custom", startDate: toReportDateString(dates[0]), endDate: toReportDateString(dates[1]) });
  closeDropdown();
}
</script>

<style scoped lang="scss">
// Report date range picker ===================================================
.report-date-range-picker {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  &__trigger {
    gap: 2px;
    display: inline-flex;
    align-items: center;
    cursor: pointer;
  }

  &__dropdown {
    min-width: 180px;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    overflow: hidden;
    background: white;
  }

  &__option {
    gap: 12px;
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    transition: background-color 0.1s ease;

    &:hover {
      background-color: rgba(0, 128, 128, 0.06);
    }

    &--selected {
      background-color: rgba(0, 128, 128, 0.08);
    }
  }

  &__calendar {
    padding: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    :deep(.dp__menu) {
      border: none;
    }
  }
}
</style>
